import { create } from "zustand";
import { defaultAttachmentMessage } from "../Components/ChatMessageComposer/ChatMessageComposer";
import CommunicationManager from "../Helper/CommunicationManager";
import Communicator from "../Helper/Communicator";
import { validateRecaptchaAction } from "../Helper/Recaptcha";
import { sanitizeHtmlHelper } from "../Helper/SanitizeHtmlHelper";
import {
  WebSocketEvent,
  WebSocketEventData,
} from "../Models/WebSocketEventData";
import { useAppViewStore } from "./AppView";
import { useFormStore } from "./Form";
import { AppMode, useRouterStore } from "./Router";
import { useSessionStore } from "./Session";
import moment from "moment";
import { HttpMessageService } from "../Services/http.clients/http.message.client";
import { HttpTicketService } from "../Services/http.clients/http.ticket.client";
import { HttpClientXHR } from "../Services/http.clients/config/http.client";
import { useConfigStore } from "./Config";

var aiQuestionToSend: string | undefined = undefined;

export const getRandomMessageId = () => {
  return new Date().toISOString() + Math.random().toString(36).substring(2, 15);
};

export const getLastUpdateDate = (ticket: Ticket) => {
  if (ticket.latestComment) {
    return moment(ticket.latestComment.createdAt);
  }

  return moment(ticket.createdAt);
};

const cleanConversationList = (newTickets: Ticket[]) => {
  // Sort tickets by updatedAt or createdAt as fallback.
  newTickets = newTickets.sort((a, b) => {
    const dateA = getLastUpdateDate(a);
    const dateB = getLastUpdateDate(b);

    return dateB.diff(dateA); // Sort in descending order (newest first)
  });

  // Remove duplicates.
  newTickets = newTickets.filter(
    (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
  );

  return newTickets;
};

export enum ChannelTypes {
  EMAIL = "email",
  WHATSAPP = "whatsapp",
  INSTAGRAM = "instagram",
  FACEBOOK = "facebook",
  WIDGET = "widget",
}

export type Ticket = {
  id?: string;
  title?: string;
  bugId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  noHumanInteraction?: boolean;
  form?: any;
  formData?: any;
  type?: string;
  status?: string;
  screenshotUrl?: string;
  screenRecordingUrl?: string;
  hidden?: boolean;
  hideContent?: boolean;
  conversationClosed?: boolean;
  generatingScreenshot?: boolean;
  sessionNotificationsUnread?: boolean;
  shareToken?: string;
  upvotesCount?: number;
  session?: {
    name: string;
    gleapId: string;
  };
  bot?: {
    active: boolean;
  };
  processingUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    id?: string;
    profileImageUrl?: string;
  };
  latestComment?: {
    data: any;
    type: string;
    createdAt: Date;
    updatedAt: Date;
    user: {
      firstName?: string;
      lastName?: string;
      email?: string;
      id?: string;
      profileImageUrl?: string;
    };
    attachments: any[];
    session: {
      name: string;
      gleapId: string;
    };
    fallbackUser: {
      username: string;
    };
  };
  channel: {
    id: string;
    type: ChannelTypes;
  };
};

export type TicketChatMessage = {
  type?: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    id?: string;
    profileImageUrl?: string;
  };
  session?: {
    gleapId?: string;
    email?: string;
    name?: string;
    userId?: string;
    id?: string;
  };
  data?: any;
  attachments?: any[];
  createdAt?: Date;
  updatedAt?: Date;
  id?: string;
  sending?: boolean;
  error?: boolean;
  sessionNotificationsUnread?: boolean;
  kaiChat?: boolean;
  rating?: "positive" | "negative";
};

interface TicketState {
  previousTickets: Ticket[];
  ticketFilter: Record<string, any>;
  loadingTickets?: boolean;
  canLoadMore?: boolean;
  currentTicket?: Ticket;
  typingUser: any;
  botWrittingText?: string;
  canSetBotWrittingText?: boolean;
  currentBotAction?: {
    action: any;
    botId: string;
    actionFlowId: string;
    actionId: number;
    lastActionSent: Date;
  };
  uploadingAttachment?: boolean;
  userTypingTimeout: any;
  currentComments: TicketChatMessage[];
  loadingComments?: boolean;
  conversationError?: string;
  currentComposerText?: string;
  unreadTickets: Ticket[];
  loadMoreConversations: (arg?: {
    preventLoading?: boolean;
    skip?: number;
  }) => void;
  setCurrentComposerText: (text: string) => void;
  setTicketUnread: (unread: boolean) => void;
  setCanLoadMore: (canLoadMore: boolean) => void;
  loadConversations: () => void;
  clearUserTyping: () => void;
  getTicketData: () => Promise<any>;
  clearUserTypingTimeout: () => void;
  setUserTyping: (user: any) => void;
  loadCurrentTicket: (shareToken: string) => void;
  clearCurrentTicket: () => void;
  getConversations: (skip: number, limit: number) => Promise<any>;
  setTicketFilter: (filter: Record<string, any>) => void;
  sendComment: (message: string, files: File[]) => void;
  loadComments: (silent?: boolean) => Promise<any>;
  rateComment: (commentId: string, rating: "positive" | "negative") => void;
  silentRefreshTickets: () => void;
  loadComment: (commentId: string, parentId: string) => void;
  getCurrentTime: () => Date;
  pushComment: (comment: TicketChatMessage) => void;
  uploadFile: (file: File) => Promise<string>;
  isTyping: (typing: boolean) => void;
  setCurrentComments: (comments: TicketChatMessage[]) => void;
  createNewConversation: (data: any, aiQuestion?: string) => Promise<any>;
  answerBotAction: (data: any, files: File[]) => Promise<any>;
  handleWebSocketEvent: (webSocketEventData: WebSocketEventData) => void;
  updateCurrentTicket: (ticketId: string, data: any) => void;
  hasAnswerBotReply: () => boolean;
  loadTicket: (shareToken: string) => Promise<any>;
  refreshCurrentTicket: () => void;
  getBotResponseCount: () => number;
  lastMessageHasContextAnswer: () => boolean;
  lastMessageIsAIAnswer: () => boolean;
  getUnreadTickets: () => void;
}

export const useTicketStore = create<TicketState>()((set, get) => ({
  previousTickets: [],
  ticketFilter: {
    status: undefined,
  },
  currentTicket: undefined,
  loadingComments: false,
  userTypingTimeout: undefined,
  typingUser: undefined,
  currentComments: [],
  uploadingAttachment: false,
  conversationError: undefined,
  currentComposerText: undefined,
  unreadTickets: [],
  hasAnswerBotReply: () => {
    return get().currentComments.some((comment) => comment.data?.answerBot);
  },
  setCurrentComments: (comments: TicketChatMessage[]) => {
    const commentsNew = comments ?? [];

    commentsNew.sort((a, b) => {
      const dateA = new Date(a.createdAt ?? new Date().toISOString());
      const dateB = new Date(b.createdAt ?? new Date().toISOString());
      return dateA.getTime() - dateB.getTime();
    });

    let botAction = null;
    if (commentsNew.length > 0) {
      const lastComment = commentsNew[commentsNew.length - 1];
      if (lastComment.type === "BOT" && lastComment.data?.action) {
        botAction = {
          action: lastComment.data?.action,
        };
      }
    }

    if (
      botAction?.action?.type === "answerbotflow" &&
      aiQuestionToSend &&
      aiQuestionToSend.length > 0
    ) {
      const aiQuestionToSendLocal = aiQuestionToSend;
      aiQuestionToSend = undefined;

      setTimeout(() => {
        // Send the pending AI question to the bot.
        get().answerBotAction(
          {
            question: aiQuestionToSendLocal,
            message: aiQuestionToSendLocal,
            aiTools: useConfigStore.getState().aiTools,
            isFirstMessage: !get().hasAnswerBotReply(),
          },
          []
        );
      }, 500);
    }

    set({
      currentBotAction: botAction as any,
      currentComments: commentsNew,
    });
  },
  setCurrentComposerText: (text: string) => {
    set({
      currentComposerText: text,
    });
  },
  clearCurrentTicket: () => {
    set({
      currentTicket: undefined,
      canSetBotWrittingText: false,
      botWrittingText: undefined,
    });
    get().setCurrentComments([]);
    get().clearUserTyping();
  },
  setTicketUnread: (unread = false) => {
    const currentTicket = get().currentTicket;

    if (currentTicket) {
      set({
        currentTicket: {
          ...currentTicket,
          sessionNotificationsUnread: unread,
        },
      });

      if (!unread) {
        set({
          unreadTickets: get().unreadTickets.filter(
            (ticket) => ticket.id !== currentTicket.id
          ),
        });
      }

      get().setCurrentComments(
        get().currentComments.map((comment) => {
          if (comment?.id === currentTicket?.id) {
            return {
              ...comment,
              sessionNotificationsUnread: unread,
            };
          }
          return comment;
        })
      );
      set({
        previousTickets: get().previousTickets.map((ticket) => {
          if (ticket?.id === currentTicket?.id) {
            return {
              ...ticket,
              sessionNotificationsUnread: unread,
            };
          }
          return ticket;
        }),
      });
    }
  },
  setCanLoadMore: (canLoadMore: boolean) => {
    set({
      canLoadMore,
    });
  },
  updateCurrentTicket: (ticketId, data) => {
    if (get().currentTicket?.id === ticketId) {
      set((state) => ({
        currentTicket: {
          ...state.currentTicket,
          ...data,
        },
      }));
    }

    var previousTickets = get().previousTickets;
    const index = previousTickets.findIndex(
      (ticket: Ticket) => ticket.id === ticketId
    );

    if (index !== -1) {
      previousTickets[index] = {
        ...previousTickets[index],
        ...data,
      };
    }

    set({
      previousTickets,
    });
  },
  getTicketData: () => {
    return new Promise((resolve, reject) => {
      CommunicationManager.getInstance()
        .sendMessageWithResolver({
          name: "collect-ticket-data",
        })
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          resolve({});
        });
    });
  },
  setTicketFilter: (filter) => {
    set({
      ticketFilter: filter,
    });

    get().loadConversations();
  },
  createNewConversation: async (data, aiQuestion?: string) => {
    try {
      aiQuestionToSend = aiQuestion;

      // Notify flow started.
      Communicator.notifyEvent("conversation-started", {
        workflowId: data.botId ?? "",
      });

      const ticketData = await get().getTicketData();

      // Open conversation loading.
      useRouterStore.getState().pushPage("conversationloading", {});

      const createdTicket = await HttpTicketService.getInstance().create({
        data: {
          botId: data.botId ?? "",
          aiQuestion: data.aiQuestion ?? false,
          isManualStart: data.isManualStart ?? false,
          ...ticketData,
        },
      });

      // Open conversations.
      useRouterStore.getState().pushPage("conversation", {
        shareToken: createdTicket.shareToken,
      });
    } catch (_) {}
  },
  answerBotAction: async (data: any, attachments: File[]) => {
    return new Promise(async (resolve, reject) => {
      const sessionStore = useSessionStore.getState();
      const shareToken = get().currentTicket?.shareToken;

      if (!shareToken || shareToken.length === 0) {
        reject(new Error("Failed"));
        return;
      }

      let action = get().currentBotAction?.action;

      // Action fallback to latest comment.
      if (!action) {
        action = get()
          .currentComments.slice()
          .reverse()
          .find((comment) => comment.type === "BOT" && comment.data?.action)
          ?.data?.action;

        if (action) {
          // Wait for one second to make sure the action is set.
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      let attachmentsUrls: any[] = [];
      try {
        for (const file of attachments) {
          try {
            const fileUrl = await get().uploadFile(file);
            attachmentsUrls.push({
              url: fileUrl,
              name: file.name,
              type: file.type,
            });
          } catch (exp) {
            console.error("Attachment upload failed:", exp);
          }
        }
      } catch (exp) {
        console.error("Error uploading attachments:", exp);
      }

      // Clear current bot action
      set({
        canSetBotWrittingText: true,
      });

      const sendAnswerAndGetTime = async (): Promise<Date> => {
        try {
          const httpClient = new HttpClientXHR();
          const response = await httpClient.post(
            `/v3/shared/tickets/${shareToken}/workflow/answer`,
            {
              ...data,
              attachments:
                attachmentsUrls.length > 0
                  ? attachmentsUrls
                  : data?.attachment
                  ? [data?.attachment]
                  : [],
            }
          );

          if (response.status === 204) {
            const serverTime = response.headers["date"];
            if (serverTime) {
              return new Date(serverTime);
            }
          }

          return get().getCurrentTime();
        } catch (error) {
          return get().getCurrentTime();
        }
      };

      let currentTime: Date;
      try {
        // Get the server time because of time issues on Windows
        currentTime = await sendAnswerAndGetTime();
      } catch (error) {
        currentTime = get().getCurrentTime();
      }

      if (action?.type === "answerbotflow" || action?.type === "aitask") {
        Communicator.playPing();

        if (data.action && data.action.length > 0) {
          get().pushComment({
            id: getRandomMessageId(),
            updatedAt: currentTime,
            createdAt: currentTime,
            session: sessionStore.session!,
            type: "BOT_REPLY",
            data: {
              content: data?.message,
              type: "text",
            },
            attachments:
              attachmentsUrls.length > 0
                ? attachmentsUrls
                : data?.attachment
                ? [data?.attachment]
                : [],
            sending: false,
          });
        } else {
          if (!get().hasAnswerBotReply() && action?.question?.length > 0) {
            get().pushComment({
              id: getRandomMessageId(),
              updatedAt: currentTime,
              createdAt: currentTime,
              type: "BOT",
              data: {
                content: action.question,
                type: "text",
              },
              sending: false,
            });
          }

          get().pushComment({
            id: getRandomMessageId(),
            updatedAt: currentTime,
            createdAt: currentTime,
            session: sessionStore.session!,
            type: "BOT_REPLY",
            data: {
              content: data.question,
              type: "text",
            },
            attachments:
              attachmentsUrls.length > 0
                ? attachmentsUrls
                : data?.attachment
                ? [data?.attachment]
                : [],
            sending: false,
          });

          get().setUserTyping({
            id: "bot",
            name: "Bot",
            avatar: "",
          });
        }
      } else if (action?.type === "calendly") {
        // Do nothing.
      } else {
        Communicator.playPing();

        // Push local message.
        get().pushComment({
          id: getRandomMessageId(),
          updatedAt: currentTime,
          createdAt: currentTime,
          session: sessionStore.session!,
          type:
            action?.type === "input" || action?.type === "rateconversation"
              ? "BOT_INPUT_REPLY"
              : "BOT_REPLY",
          data: {
            content: data.message,
            rating: data.rating,
            type: action?.type ?? "text",
            question: action?.title ?? action?.placeholder ?? "",
          },
          attachments:
            attachmentsUrls.length > 0
              ? attachmentsUrls
              : data?.attachment
              ? [data?.attachment]
              : [],
          sending: true,
        });
      }

      // Update bot with new action.
      const updatedComments = get().currentComments.map((comment) => {
        if (comment.sending) {
          comment.sending = false;
        }
        return comment;
      });

      get().setCurrentComments(updatedComments);

      resolve({});
    });
  },
  uploadFile(file) {
    return new Promise((resolve, reject) => {
      if (file.size / 1024 / 1024 > 100) {
        reject(new Error("File is too big. Max size is 100MB."));
        return;
      }

      const sessionStore = useSessionStore.getState();

      var formData = new FormData();
      formData.append("file", file);

      var xhr = new XMLHttpRequest();
      xhr.open("POST", sessionStore.apiUrl + "/uploads/attachments");
      if (sessionStore.session) {
        xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
        xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
        xhr.setRequestHeader(
          "Gleap-Hash",
          sessionStore.session.gleapHash ?? ""
        );
      }
      xhr.onerror = function () {
        reject(new Error("Upload failed"));
      };
      xhr.onreadystatechange = function () {
        if (xhr.readyState == 4 && xhr.status == 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.fileUrls && data.fileUrls.length > 0) {
              const dataUrl = data.fileUrls[0];
              resolve(dataUrl);
            } else {
              reject(new Error("Upload failed"));
            }
          } catch (exp) {
            reject(new Error("Upload failed"));
          }
        }
      };

      xhr.send(formData);
    });
  },
  getCurrentTime: () => {
    try {
      const comments = get().currentComments;
      const lastComment = comments[comments.length - 1];
      if (!lastComment) {
        return new Date();
      }

      const lastCommentDate = moment.utc(lastComment.createdAt).toDate();

      if (
        lastComment?.createdAt &&
        moment.utc(lastCommentDate).isBefore(moment.utc())
      ) {
        return moment.utc().add(1, "milliseconds").toDate();
      }
      const date = new Date(lastComment.createdAt ?? new Date().toISOString());
      date.setUTCMilliseconds(date.getUTCMilliseconds() + 1);
      return date;
    } catch {
      return new Date();
    }
  },
  loadTicket: async (shareToken: string) => {
    try {
      const ticket = await HttpTicketService.getInstance().findOne({
        id: shareToken,
      });

      return ticket;
    } catch (_) {}
  },
  refreshCurrentTicket: () => {
    const oldTicketShareToken = get().currentTicket?.shareToken;
    if (oldTicketShareToken) {
      get()
        .loadTicket(oldTicketShareToken)
        .then((data) => {
          set({
            currentTicket: data,
          });
        });
    }
  },
  loadCurrentTicket: (shareToken: string) => {
    const oldTicketShareToken = get().currentTicket?.shareToken;
    if (oldTicketShareToken !== shareToken) {
      get()
        .loadTicket(shareToken)
        .then((data) => {
          try {
            get().clearUserTyping();

            set({
              currentTicket: data,
              loadingComments: true,
            });
            get().setCurrentComments([]);

            // Load comments.
            get()
              .loadComments()
              .then(() => {});
          } catch (exp) {}
        });
    }
  },
  loadComments: async (silent = false) => {
    const { currentTicket } = get();
    if (!currentTicket) {
      return Promise.reject();
    }

    if (!silent) {
      set({
        loadingComments: true,
      });
    }

    try {
      const comments = await HttpMessageService.getInstance().find({
        query: {
          ticketShareToken: currentTicket.shareToken,
        },
      });

      get().setCurrentComments(comments);
    } catch (_) {}

    set({
      loadingComments: false,
    });
  },
  rateComment: async (commentId: string, rating: "positive" | "negative") => {
    const { currentTicket } = get();
    if (!currentTicket) {
      return;
    }

    // Locally update rating.
    get().setCurrentComments(
      get().currentComments.map((comment) => {
        if (comment.id === commentId) {
          comment.rating = rating;
        }
        return comment;
      })
    );

    try {
      await HttpMessageService.getInstance().rateMessage({
        messageId: commentId,
        rating,
      });
    } catch (_) {}
  },
  isTyping: async (typing: boolean) => {
    const { currentTicket } = get();
    if (!currentTicket || !currentTicket.shareToken) {
      return;
    }

    try {
      await HttpTicketService.getInstance().indicateTyping({
        ticketShareToken: currentTicket.shareToken,
        typing,
      });
    } catch (_) {}
  },
  loadComment: async (commentId: string, parentId: string) => {
    const currentTicket = get().currentTicket;
    if (!currentTicket) {
      return;
    }

    try {
      const fetchedComment = await HttpMessageService.getInstance().findOne({
        id: commentId,
        query: {
          ticketShareToken: currentTicket.shareToken,
        },
      });

      var newComments = get().currentComments.filter((comment) => {
        return !comment.sending;
      });

      if (currentTicket?.id === parentId) {
        // Check if comment already exists.
        const index = newComments.findIndex(
          (comment) => comment.id === fetchedComment.id
        );
        if (index === -1) {
          newComments.push(fetchedComment);
        }

        get().setCurrentComments(newComments);
      }
    } catch (_) {}
  },
  pushComment: (comment: TicketChatMessage) => {
    get().setCurrentComments([...get().currentComments, comment]);
  },
  sendComment: async (message: string, attachments: File[]) => {
    const { session, apiUrl } = useSessionStore.getState();
    const { currentTicket } = get();
    const fakeMessageId = getRandomMessageId();

    if (!currentTicket || !session) {
      return;
    }

    Communicator.playPing();

    set({
      uploadingAttachment: true,
    });

    get().pushComment({
      id: fakeMessageId,
      updatedAt: get().getCurrentTime(),
      createdAt: get().getCurrentTime(),
      session: session,
      type: "SHARED_COMMENT",
      data: {
        content: sanitizeHtmlHelper(message),
      },
      sending: true,
    });

    try {
      var attachmentsUrls: any[] = [];
      for (const file of attachments) {
        try {
          const fileUrl = await get().uploadFile(file);
          attachmentsUrls.push({
            url: fileUrl,
            name: file.name,
            type: file.type,
          });
        } catch (exp) {
          alert("File upload failed. File too big.");
        }
      }

      if (attachmentsUrls.length <= 0 && message === defaultAttachmentMessage) {
        try {
          set({
            uploadingAttachment: false,
          });
          get().setCurrentComments(
            get().currentComments.map((comment) => {
              if (comment.sending) {
                comment.sending = false;
                comment.data.content = "❌ File upload failed. File too big.";
              }
              return comment;
            })
          );
        } catch (exp) {}

        return;
      }

      const updateError = () => {
        // Set message sending as failed.
        const updatedComments = get().currentComments.map((comment) => {
          if (comment.sending) {
            comment.sending = false;
            comment.error = true;
          }
          return comment;
        });

        get().setCurrentComments(updatedComments);
      };

      const token = await validateRecaptchaAction("sendchatmessage");

      try {
        await HttpMessageService.getInstance().create({
          data: {
            comment: message,
            attachments: attachmentsUrls,
            recaptchaToken: token,
            ticketShareToken: currentTicket.shareToken,
          },
        });

        set({
          uploadingAttachment: false,
        });
        get().setCurrentComments(
          get().currentComments.map((comment) => {
            if (comment.id === fakeMessageId) {
              comment.sending = false;
              comment.attachments = attachmentsUrls;
            }
            return comment;
          })
        );
      } catch (err) {
        updateError();
      }
    } catch (exp) {}
  },
  loadConversations: () => {
    set({
      previousTickets: [],
      loadingTickets: false,
      canLoadMore: true,
    });

    get().getUnreadTickets();

    get().loadMoreConversations();
  },
  getUnreadTickets: async () => {
    try {
      const { session, sdkKey } = useSessionStore.getState();
      if (!session || !sdkKey) {
        return;
      }

      // Load all tickets without filters for notification check.
      const tickets = await HttpTicketService.getInstance().find({
        query: {
          filter: JSON.stringify({
            sessionNotificationsUnread: true,
          }),
          limit: 10,
          skip: 0,
        },
      });

      set({
        unreadTickets: tickets,
      });
    } catch (error) {
      console.error("Error getting unread tickets", error);
    }
  },
  silentRefreshTickets: () => {
    get()
      .getConversations(0, 25)
      .then((data) => {
        let newTickets = [...data, ...get().previousTickets].filter(
          (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
        );

        newTickets = cleanConversationList(newTickets);

        set({
          previousTickets: newTickets,
        });
      })
      .catch(() => {});
  },
  getConversations: async (skip = 0, limit = 25) => {
    try {
      let query: any = {
        limit,
        skip,
      };

      if (get().ticketFilter.status) {
        if (get().ticketFilter.status === "OPEN") {
          query = {
            ...query,
            status: { $nin: ["CLOSED", "DONE"] },
          };
        } else {
          query = {
            ...query,
            status: get().ticketFilter.status,
          };
        }
      }

      query = {
        filter: JSON.stringify({
          ...query,
        }),
      };

      const tickets = await HttpTicketService.getInstance().find({
        query,
      });

      return tickets;
    } catch (_) {
      return [];
    }
  },
  loadMoreConversations: (args) => {
    const { session, sdkKey } = useSessionStore.getState();
    if (get().loadingTickets || !get().canLoadMore || !session || !sdkKey) {
      return;
    }

    if (args?.preventLoading) {
      set({
        loadingTickets: true,
      });
    }

    const skip = args?.skip ?? get().previousTickets.length;

    get()
      .getConversations(skip, 25)
      .then((data) => {
        var newTickets: any[] = [];
        if (data && data.length > 0) {
          newTickets = [...get().previousTickets];
          newTickets = newTickets.filter(function (el) {
            return data.indexOf(el) < 0;
          });
          newTickets = [...newTickets, ...data];
        } else {
          newTickets = [...get().previousTickets];
        }

        newTickets = cleanConversationList(newTickets);

        set({
          previousTickets: newTickets,
          loadingTickets: false,
          canLoadMore: data.length > 0,
        });
      })
      .catch(() => {});
  },
  clearUserTyping: () => {
    get().clearUserTypingTimeout();
    set({
      typingUser: undefined,
    });
  },
  clearUserTypingTimeout: () => {
    const timeout = get().userTypingTimeout;
    if (timeout) {
      clearTimeout(timeout);
    }
  },
  setUserTyping: (user: any) => {
    get().clearUserTypingTimeout();
    set({
      typingUser: user,
      userTypingTimeout: setTimeout(() => {
        set({
          userTypingTimeout: undefined,
          typingUser: undefined,
        });
      }, 20000),
    });
  },
  handleWebSocketEvent: (webSocketEventData: WebSocketEventData) => {
    switch (webSocketEventData.action) {
      case WebSocketEvent.MESSAGE_CREATED:
        Communicator.playPing();

        if (get().currentTicket?.id !== webSocketEventData.parentID) {
          return;
        }

        if (!CommunicationManager.isWidgetOpen) {
          return;
        }

        set({
          botWrittingText: undefined,
          canSetBotWrittingText: false,
        });
        get().clearUserTyping();

        // Load new comment.
        get().loadComment(
          webSocketEventData.actionID,
          webSocketEventData.parentID
        );
        break;
      case WebSocketEvent.MESSAGE_DELETED:
        if (get().currentTicket?.id !== webSocketEventData.parentID) {
          return;
        }

        get().setCurrentComments(
          get().currentComments.filter(
            (comment) => comment.id !== webSocketEventData.actionID
          )
        );

        break;
      case WebSocketEvent.BUG_TYPING:
        if (
          get().currentTicket?.id === webSocketEventData.actionData?.ticketId
        ) {
          if (
            webSocketEventData.actionData?.typing &&
            webSocketEventData.actionData?.user
          ) {
            get().setUserTyping(webSocketEventData.actionData?.user);
          } else {
            get().clearUserTyping();
          }
        }
        break;

      case WebSocketEvent.BOT_WRITTING:
        const conversationId = webSocketEventData?.actionData?.conversationId;
        if (
          get().currentTicket?.id === conversationId &&
          get().canSetBotWrittingText
        ) {
          const botWritting = webSocketEventData?.actionData?.message;
          set({
            botWrittingText: botWritting,
          });
        }
        break;

      case WebSocketEvent.BOT_TOOL:
        const toolName = webSocketEventData?.actionData?.name;
        if (toolName && toolName.length > 0) {
          // Send to communicator.
          CommunicationManager.getInstance().sendMessage({
            name: "tool-execution",
            data: {
              name: toolName,
              params: webSocketEventData?.actionData?.params ?? {},
            },
          });
        }
        break;

      case WebSocketEvent.CUSTOMACTION_RUN:
        Communicator.runCustomAction(
          webSocketEventData?.actionData?.actionName,
          webSocketEventData?.actionData?.shareToken
        );

        if (webSocketEventData?.actionData?.closeWidget ?? true) {
          Communicator.closeWidget();
        }
        break;

      case WebSocketEvent.BUG_CREATED:
        // Reload conversations.
        get().silentRefreshTickets();
        break;

      case WebSocketEvent.BUG_UPDATED:
        if (webSocketEventData?.actionData?.update) {
          get().updateCurrentTicket(
            webSocketEventData?.actionID,
            webSocketEventData?.actionData?.update
          );

          // Check if ticket is unread.
          if (
            webSocketEventData?.actionData?.update
              ?.sessionNotificationsUnread &&
            !get().currentTicket
          ) {
            get().getUnreadTickets();
          }
        }
        break;

      case WebSocketEvent.BOT_OPENLINK:
        if (webSocketEventData?.actionData?.href) {
          Communicator.openURL(
            webSocketEventData?.actionData?.href,
            webSocketEventData?.actionData?.newTab
          );
        }
        break;

      case WebSocketEvent.BOT_STARTFEEDBACKFLOW:
        const flow = webSocketEventData?.actionData?.flow;
        if (webSocketEventData?.actionData?.flow) {
          useFormStore.setState({
            feedbackFlow: flow,
            isSurveyFlow: false,
          });
          useRouterStore.getState().setPage(
            "flow",
            {
              flowId: flow,
            },
            AppMode.WIDGET
          );
          useAppViewStore.getState().reset();
        }
        break;

      default:
        break;
    }
  },
  lastMessageHasContextAnswer: () => {
    try {
      const lastComment =
        get().currentComments[get().currentComments.length - 2];

      return lastComment?.data?.contextMissing ?? false;
    } catch (exp) {}

    return true;
  },
  lastMessageIsAIAnswer: () => {
    try {
      const lastComment =
        get().currentComments[get().currentComments.length - 2];

      return (
        lastComment?.type === "BOT" && lastComment?.data?.answerBot === true
      );
    } catch (exp) {}

    return false;
  },
  getBotResponseCount: () => {
    return get().currentComments.filter(
      (comment) => comment.type === "BOT" && comment?.data?.answerBot === true
    ).length;
  },
}));
